import React, { useState, useEffect } from 'react';
import {
  Box, Select, MenuItem, Paper, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Snackbar, Alert, Tooltip
} from '@mui/material';
import { WhatsApp } from '@mui/icons-material';
import axios from 'axios';

interface MatchReport {
  id: number;
  pharmacyNum: string;
  updDate: string | null;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

const EditCheck: React.FC = () => {
  const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonth());
  const [reportData, setReportData] = useState<MatchReport[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    document.title = 'Проверка на изменения мат.отчетов | Zerde';
  }, []);

  useEffect(() => {
    fetchReportData();
  }, [selectedMonth]);

  function getCurrentMonth(): string {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }

  function getMonthsList(): { value: string; label: string }[] {
    const months = [];
    const currentDate = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleString('ru', { year: 'numeric', month: 'long' });
      months.push({ value, label });
    }
    return months;
  }

  const copyToWhatsApp = async () => {
    const pharmacyList = reportData.map(item => item.pharmacyNum).join(', ');
    const message = `Следующие аптеки должны сдать мат. отчет ещё раз, в срочном порядке:\n${pharmacyList}`;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(message);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = message;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          document.execCommand('copy');
          document.body.removeChild(textArea);
        } catch (err) {
          document.body.removeChild(textArea);
          throw new Error('Fallback: Copying text failed');
        }
      }

      setSnackbar({
        open: true,
        message: 'Текст скопирован в буфер обмена',
        severity: 'success',
      });
    } catch (err) {
      console.error('Copy failed:', err);
      setSnackbar({
        open: true,
        message: `Ошибка при копировании текста: ${(err as Error).message || 'Неизвестная ошибка'}`,
        severity: 'error',
      });
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const [year, month] = selectedMonth.split('-');

      const response = await axios.get('https://tbl.zerdegroup.kz/api/edit-check', {
        params: {
          month: Number(month),
          year: Number(year)
        }
      });

      if (response.data) {
        setReportData(response.data);
        setSnackbar({
          open: true,
          message: `Найдено записей: ${response.data.length} `,
          severity: 'success',
        });
      }
    } catch (error) {
      console.error('Ошибка при получении данных:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при получении данных: ' + error,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCheck = async (id: number) => {
    try {
      await axios.post('https://tbl.zerdegroup.kz/api/mark-checked', { id });

      setReportData(prevData => prevData.filter(item => item.id !== id));

      setSnackbar({
        open: true,
        message: 'Запись успешно отмечена',
        severity: 'success',
      });

      const remainingInPage = reportData.length - 1;
      if (remainingInPage % rowsPerPage === 0 && page > 0) {
        setPage(prev => prev - 1);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Ошибка при обновлении записи: ' + error,
        severity: 'error',
      });
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handeCheckAll = async () => {
    try {
      const [year, month] = selectedMonth.split('-');
      await axios.post('https://tbl.zerdegroup.kz/api/mark-all-checked', {
        month: Number(month),
        year: Number(year)
      });
  
      await fetchReportData();
  
      setSnackbar({
        open: true,
        message: 'Все записи успешно отмечены',
        severity: 'success',
      });
    } catch (error) {
      console.error('Ошибка при отметке всех записей:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при отметке всех записей: ' + error,
        severity: 'error',
      });
    }
  };

  return (
    <Box sx={{ maxWidth: '100%', overflowX: 'auto' }}>
      <Box sx={{ display: 'flex', mb: 2, justifyContent: 'space-between' }}>
        <Box>
          <Select
            size='small'
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            {getMonthsList().map((month) => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </Select>

          <Tooltip title="Получить текст сообщения для WhatsApp" enterDelay={1000} arrow>
            <span>
              <Button
                variant="contained"
                color="success"
                onClick={copyToWhatsApp}
                disabled={reportData.length === 0}
                size="large"
                sx={{ ml: 2 }}
              >
                <WhatsApp />
              </Button>
            </span>
          </Tooltip>
        </Box>
        <Box>
          <Button
            variant='contained'
            color='primary'
            size="large"
            onClick={handeCheckAll}
          >
            Отметить всё
          </Button>
        </Box>
      </Box>

      {reportData.length > 0 && (
        <Paper sx={{ marginTop: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>№</TableCell>
                  <TableCell>Номер аптеки</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow key={row.id}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{row.pharmacyNum}</TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          sx={{ background: 'darkgreen' }}
                          onClick={() => handleCheck(row.id)}
                        >
                          Проверено
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={reportData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditCheck;